import { IonInput, useIonViewDidEnter } from "@ionic/react";
import { forwardRef, useRef } from "react";
import { useTranslation } from "react-i18next";
import { type AnyFieldApi } from "@tanstack/react-form";
import { cx } from "@/lib/style/cva.config";
import { mergeRefs } from "@/utils/merge-refs";
import { getErrorText } from "./utils";

export type FormInputProps = {
  field: AnyFieldApi;
  autofocusOnIonViewDidEnter?: boolean;
  stacked?: boolean;
};

export const FormInput = forwardRef<
  HTMLIonInputElement,
  React.ComponentProps<typeof IonInput> & FormInputProps
>(function FormInput({ field, autofocusOnIonViewDidEnter, stacked = true, ...props }, ref) {
  const { i18n } = useTranslation();
  const inputRef = useRef<HTMLIonInputElement>(null);

  useIonViewDidEnter(() => {
    if (autofocusOnIonViewDidEnter) {
      inputRef.current?.setFocus();
    }
  });

  return (
    <IonInput
      ref={mergeRefs([ref, inputRef])}
      name={field.name}
      type={props.type}
      errorText={getErrorText({ language: i18n.language, errors: field.state.meta.errors })}
      value={field.state.value}
      onIonChange={(event) => field.handleChange(String(event.detail.value))}
      onIonBlur={field.handleBlur}
      readonly={field.state.meta.isValidating || field.form.state.isSubmitting}
      labelPlacement={stacked ? "stacked" : undefined}
      clearInput={false}
      clearOnEdit={false}
      autocapitalize="sentences"
      {...props}
      className={cx(
        stacked && "stacked",
        field.state.meta.isTouched && "ion-touched",
        !!field.state.meta.errors && field.state.meta.errors.length > 0 && "ion-invalid",
        props.className,
      )}
    />
  );
});
